import { holdsAnywhere } from '@carii/casl-abilities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { useAbility } from '../../context/AbilityContext.js';
import { type BulkCodeResponse, discountsApi } from '../../services/discounts.js';
import { CodeBulkResultDialog } from './CodeBulkResultDialog.js';
import {
  AskConsequences,
  CancelReasonDialog,
  RejectConsequences,
  askTitle,
  rejectTitle,
} from './CodeCancelCells.js';
import { ShareCodesDialog } from './ShareCodesDialog.js';

interface SharedActionBarProps {
  discountId: string;
  selectedIds: Set<string>;
  /** 391: id → code text, so the results name each code. */
  codeLabels: Record<string, string>;
  /** 411: the selected codes whose pending cancellation this caller may decide (the platform's answer). */
  cancelDecisionIds: string[];
  onClear: () => void;
}

/**
 * 212 (#307) → 391 (#1312): one bulk action on the selection. "Share…" opens the share dialog (a customer, an optional
 * booking of theirs, a reason; each share waits for approval). "Unshare" returns codes to unshared; their history keeps
 * the share. 411 (#1513): "Cancel codes…" asks to cancel the selection; a SuperAdmin approves or rejects the selected
 * codes the platform says they may decide.
 */
export function SharedActionBar({
  discountId,
  selectedIds,
  codeLabels,
  cancelDecisionIds,
  onClear,
}: SharedActionBarProps) {
  const ability = useAbility();
  const queryClient = useQueryClient();
  const [sharing, setSharing] = useState(false);
  const [unsharing, setUnsharing] = useState(false);
  const [result, setResult] = useState<BulkCodeResponse | null>(null);
  // 411 (#1513): ask to cancel the selection; a SuperAdmin decides the selected codes they may decide.
  const [asking, setAsking] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const ask = useMutation({
    mutationFn: (reason: string) =>
      discountsApi.askCancelCodes(discountId, [...selectedIds], reason),
    onSuccess: (data) => {
      setAsking(false);
      setResult(data);
    },
  });
  const approveCancel = useMutation({
    mutationFn: () => discountsApi.approveCancelCodes(discountId, cancelDecisionIds),
    onSuccess: (data) => setResult(data),
  });
  const rejectCancel = useMutation({
    mutationFn: (reason: string) =>
      discountsApi.rejectCancelCodes(discountId, cancelDecisionIds, reason),
    onSuccess: (data) => {
      setRejecting(false);
      setResult(data);
    },
  });

  const unshare = useMutation({
    mutationFn: () => discountsApi.unshareCodes(discountId, [...selectedIds]),
    onSuccess: (data) => {
      setUnsharing(false);
      setResult(data);
    },
  });

  function finished() {
    queryClient.invalidateQueries({ queryKey: ['discount-codes', discountId] });
    onClear();
  }

  // 372: the server asks for `update` (Admin holds it through manage).
  if (!holdsAnywhere(ability as never, 'update', 'Discount') || selectedIds.size === 0) return null;

  const count = selectedIds.size;

  return (
    <div
      data-testid="shared-action-bar"
      className="mb-3 flex flex-wrap items-center gap-3 rounded-xl shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] border border-hairline bg-surface px-4 py-2 text-sm"
    >
      <span className="font-medium text-ink">{count} selected</span>
      <Button size="sm" variant="secondary" onClick={onClear}>
        Clear
      </Button>
      <Button size="sm" onClick={() => setSharing(true)}>
        Share…
      </Button>
      <Button size="sm" variant="secondary" onClick={() => setUnsharing(true)}>
        Unshare
      </Button>
      <Button size="sm" variant="secondary" onClick={() => setAsking(true)}>
        Cancel codes…
      </Button>
      {cancelDecisionIds.length > 0 && (
        <>
          <Button
            size="sm"
            loading={approveCancel.isPending}
            onClick={() => approveCancel.mutate()}
          >
            Approve cancellation
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setRejecting(true)}>
            Reject cancellation…
          </Button>
        </>
      )}
      {approveCancel.isError && (
        <span role="alert" className="text-red-600">
          {(approveCancel.error as { message?: string }).message || 'Could not approve.'}
        </span>
      )}

      <CancelReasonDialog
        open={asking}
        title={askTitle(count)}
        label="Why cancel these codes?"
        confirmLabel="Ask to cancel"
        confirmVariant="danger"
        pending={ask.isPending}
        error={ask.isError ? ask.error : null}
        onClose={() => setAsking(false)}
        onConfirm={(reason) => ask.mutate(reason)}
      >
        <AskConsequences />
      </CancelReasonDialog>

      <CancelReasonDialog
        open={rejecting}
        title={rejectTitle(cancelDecisionIds.length)}
        label="Why reject?"
        confirmLabel="Reject"
        confirmVariant="primary"
        pending={rejectCancel.isPending}
        error={rejectCancel.isError ? rejectCancel.error : null}
        onClose={() => setRejecting(false)}
        onConfirm={(reason) => rejectCancel.mutate(reason)}
      >
        <RejectConsequences count={cancelDecisionIds.length} />
      </CancelReasonDialog>

      <ShareCodesDialog
        open={sharing}
        discountId={discountId}
        codeIds={[...selectedIds]}
        codeLabels={codeLabels}
        onClose={() => setSharing(false)}
        onDone={() => {
          setSharing(false);
          finished();
        }}
      />

      <Modal open={unsharing} onClose={() => setUnsharing(false)} title="Unshare codes">
        <div className="space-y-4 px-6 py-4 text-sm text-ink">
          <p>
            Unshare <strong>{count}</strong> code{count === 1 ? '' : 's'}? Anyone can then use{' '}
            {count === 1 ? 'it' : 'them'}. Used codes stay shared; each code keeps its history.
          </p>
          {unshare.isError && (
            <p role="alert" className="text-red-600">
              {(unshare.error as { message?: string }).message ?? 'Could not unshare the codes.'}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="secondary" onClick={() => setUnsharing(false)}>
              Cancel
            </Button>
            <Button size="sm" loading={unshare.isPending} onClick={() => unshare.mutate()}>
              Unshare
            </Button>
          </div>
        </div>
      </Modal>

      <CodeBulkResultDialog
        result={result}
        labels={codeLabels}
        onClose={() => {
          setResult(null);
          finished();
        }}
      />
    </div>
  );
}
